import { cn, HoverCard, HoverCardContent, HoverCardTrigger } from '@myosh/odin-components';
import { useTranslation } from 'react-i18next';
import { fromIsoDate } from '@/utils/date-ranges';
import type { RecordStatus, ScheduleRecord } from '@/utils/schedule-records';

const STATUS_ORDER: RecordStatus[] = ['overdue', 'progress', 'completed', 'future'];

const statusStyles: Record<RecordStatus, string> = {
  overdue: 'bg-error/15 text-error',
  progress: 'bg-warning/15 text-warning',
  completed: 'bg-success/15 text-success',
  future: 'bg-info/15 text-info',
};

interface StatusGroup {
  status: RecordStatus;
  records: ScheduleRecord[];
}

const groupByStatus = (records: ScheduleRecord[]): StatusGroup[] =>
  STATUS_ORDER.flatMap((status) => {
    const matching = records.filter((record) => record.status === status);
    return matching.length > 0 ? [{ status, records: matching }] : [];
  });

const useStatusLabel = () => {
  const { t } = useTranslation();
  const labels: Record<RecordStatus, string> = {
    overdue: t(($) => $.statusOverdue),
    progress: t(($) => $.statusInProgress),
    completed: t(($) => $.statusCompleted),
    future: t(($) => $.statusPlanned),
  };
  return (status: RecordStatus) => labels[status];
};

const formatDate = (record: ScheduleRecord, dateFormat: Intl.DateTimeFormat) =>
  dateFormat.format(fromIsoDate(record.date));

// What the entry's own text leaves out: the date once a document number names the record, and the workflow status.
const recordDetails = (record: ScheduleRecord, dateFormat: Intl.DateTimeFormat) =>
  [record.docNumber === undefined ? '' : formatDate(record, dateFormat), record.label].filter(Boolean).join(' · ');

const RecordEntry = ({ record, dateFormat }: { record: ScheduleRecord; dateFormat: Intl.DateTimeFormat }) => {
  const { t } = useTranslation();
  const text = record.docNumber === undefined ? formatDate(record, dateFormat) : `#${record.docNumber}`;
  const details = recordDetails(record, dateFormat);
  const className = cn('rounded-md px-1.5 py-0.5 text-sm', statusStyles[record.status]);

  if (record.status === 'future' || !record.link) {
    return (
      <span title={details || undefined} className={className}>
        {text}
      </span>
    );
  }
  return (
    <a
      href={record.link}
      target="_blank"
      rel="noreferrer"
      title={[details, t(($) => $.openRecordInViking)].filter(Boolean).join('\n')}
      className={cn(className, 'underline-offset-2 hover:underline')}
    >
      {text}
    </a>
  );
};

interface StatusTagProps {
  group: StatusGroup;
  isLabelled: boolean;
  dateFormat: Intl.DateTimeFormat;
}

const StatusTag = ({ group, isLabelled, dateFormat }: StatusTagProps) => {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  const summary = `${statusLabel(group.status)}: ${t(($) => $.recordsCount, { count: group.records.length })}`;

  return (
    <HoverCard>
      <HoverCardTrigger
        render={
          <button
            type="button"
            aria-label={summary}
            className={cn(
              'max-w-full truncate rounded-md px-2 py-0.5 font-medium',
              isLabelled ? 'text-xs' : 'text-sm',
              statusStyles[group.status]
            )}
          />
        }
      >
        {isLabelled ? `${statusLabel(group.status)} ${group.records.length}` : group.records.length}
      </HoverCardTrigger>
      <HoverCardContent className="w-auto">
        <p className="mb-2 font-medium text-sm">{summary}</p>
        <ul className="flex max-h-64 flex-col gap-1 overflow-auto">
          {group.records.map((record) => {
            const details = recordDetails(record, dateFormat);
            return (
              <li key={record.id} className="flex items-center gap-2">
                <RecordEntry record={record} dateFormat={dateFormat} />
                {details && <span className="text-muted-foreground text-sm">{details}</span>}
              </li>
            );
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
};

interface RecordCountCellProps {
  records: ScheduleRecord[];
  locale: string;
}

// One record shows its document number, or its date without one; several of one status a count; a mix shows one
// labelled count per status.
export const RecordCountCell = ({ records, locale }: RecordCountCellProps) => {
  const dateFormat = new Intl.DateTimeFormat(locale);
  const [first, ...rest] = records;

  if (!first) {
    return null;
  }
  if (rest.length === 0) {
    return <RecordEntry record={first} dateFormat={dateFormat} />;
  }

  const groups = groupByStatus(records);
  const isMixed = groups.length > 1;

  return (
    <div className="flex flex-col items-start gap-1">
      {groups.map((group) => (
        <StatusTag key={group.status} group={group} isLabelled={isMixed} dateFormat={dateFormat} />
      ))}
    </div>
  );
};
